import { useEffect, useRef, useState } from "react";
import { Reveal, Eyebrow, SectionTitle, WhatsAppIcon } from "./shared";
import { useSite } from "../../context/SiteContext";
import { imgUrl, waLink, optImg } from "../../lib/api";
import { MapPin, Clock, Play } from "lucide-react";

export default function Parish() {
  const { content } = useSite();
  const mapRef = useRef(null);
  const [mapVisible, setMapVisible] = useState(false);
  const p = content?.parish;

  // Carrega o Google Maps só quando a seção se aproxima da viewport (economiza ~250KB de JS no boot)
  useEffect(() => {
    const el = mapRef.current;
    if (!el || mapVisible) return;
    const ob = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapVisible(true);
          ob.disconnect();
        }
      },
      { rootMargin: "600px" }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [mapVisible, p]);

  if (!p) return null;
  const c = content.contacts;

  return (
    <section id="paroquia" className="py-20 lg:py-28 bg-sand" data-testid="parish-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>{p.section_title}</Eyebrow>
              <SectionTitle>{p.title}</SectionTitle>
            </Reveal>
            {(p.text || "").split("\n\n").filter(Boolean).map((par, i) => (
              <Reveal key={i} delay={0.1 + i * 0.1}>
                <p className="mt-6 text-base sm:text-lg font-light text-cocoa leading-relaxed">{par}</p>
              </Reveal>
            ))}

            <Reveal delay={0.25}>
              <div className="mt-8 bg-warmwhite border border-gold/25 rounded-2xl p-6 sm:p-8 gold-frame" data-testid="parish-card">
                <p className="font-serif text-xl text-olive" data-testid="parish-name">{p.name}</p>
                <p className="mt-3 flex items-start gap-2 text-sm text-cocoa">
                  <MapPin className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                  <span data-testid="parish-address">{p.address}</span>
                </p>
                <p className="mt-2 flex items-start gap-2 text-sm text-cocoa">
                  <Clock className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                  <span data-testid="parish-masses"><strong className="font-semibold">Missas:</strong> {p.masses}</span>
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-8 flex flex-wrap gap-3">
                {p.watch_url && (
                  <a
                    data-testid="parish-watch-button"
                    href={p.watch_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-olive/30 text-olive hover:bg-olive hover:text-cream font-semibold text-sm tracking-wide px-6 py-3.5 rounded-full transition-all duration-300"
                  >
                    <Play className="w-4 h-4" /> ASSISTA A UMA CELEBRAÇÃO COM PADRE LUCAS
                  </a>
                )}
                <a
                  data-testid="parish-directions-button"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.maps_query || p.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-olive/30 text-olive hover:bg-olive hover:text-cream font-semibold text-sm tracking-wide px-6 py-3.5 rounded-full transition-all duration-300"
                >
                  <MapPin className="w-4 h-4" /> COMO CHEGAR
                </a>
                <a
                  data-testid="parish-whatsapp-button"
                  href={waLink(c.whatsapp_number, "Olá, Padre Lucas. Gostaria de conversar e conhecer seu trabalho na paróquia.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => window.gtag && window.gtag("event", "click_whatsapp", { location: "parish" })}
                  className="inline-flex items-center gap-2 bg-whatsapp hover:bg-whatsappdark text-white font-semibold text-sm tracking-wide px-6 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.02]"
                >
                  <WhatsAppIcon className="w-4 h-4" /> FALAR COM PADRE LUCAS
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 space-y-5">
            <div className="grid grid-cols-2 gap-5">
              <Reveal>
                <img
                  src={optImg(p.photo_church, 1000)}
                  alt="Paróquia Santo Expedito e Nossa Senhora Desatadora dos Nós, Esmeraldas/MG"
                  className="w-full aspect-[3/4] object-cover rounded-2xl gold-frame"
                  loading="lazy"
                  data-testid="parish-photo-church"
                />
              </Reveal>
              <Reveal delay={0.12}>
                <img
                  src={optImg(p.photo_celebrating, 800)}
                  alt="Frei Lucas Macieira celebrando"
                  className="w-full aspect-[3/4] object-cover rounded-2xl gold-frame mt-8"
                  loading="lazy"
                  data-testid="parish-photo-celebrating"
                />
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div ref={mapRef} className="rounded-2xl overflow-hidden border border-gold/25 bg-beige" data-testid="parish-map">
                {mapVisible ? (
                  <iframe
                    title="Mapa da Paróquia Santo Expedito e Nossa Senhora Desatadora dos Nós"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(p.maps_query || p.address)}&output=embed`}
                    className="w-full h-64 sm:h-72 border-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-64 sm:h-72 flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-gold" aria-hidden="true" />
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
